import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  Avatar,
  IconButton,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Collapse,
  Tooltip,
  Popover,
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Group as GroupIcon,
} from '@mui/icons-material';
import api from '../services/api';

const COLORS = ['#6366f1','#0d9488','#f59e0b','#ef4444','#8b5cf6','#10b981','#3b82f6','#f97316'];
const colorFor = (name) => COLORS[(name || '').charCodeAt(0) % COLORS.length];

function UserAvatar({ name, size = 26 }) {
  const initial = (name || '?').trim().slice(0, 1).toUpperCase();
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: colorFor(name), color: '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.42, fontWeight: 700, flexShrink: 0,
    }}>
      {initial}
    </div>
  );
}

// ── Custom "Add Extra Resource" modal ─────────────────────────────────────────
function AddResourceModal({ open, onClose, allUsers, usersLoading, selectedUser, setSelectedUser, notes, setNotes, onAdd, addLoading, error, onClearError }) {
  const [dropOpen, setDropOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropRef = useRef(null);

  useEffect(() => {
    if (!open) { setSearch(''); setDropOpen(false); }
  }, [open]);

  useEffect(() => {
    if (!dropOpen) return;
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setDropOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [dropOpen]);

  const filtered = allUsers.filter(u =>
    !search || (u.name || '').toLowerCase().includes(search.toLowerCase()) || (u.department || '').toLowerCase().includes(search.toLowerCase())
  );

  if (!open) return null;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(15,23,42,0.45)', backdropFilter: 'blur(2px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        width: '100%', maxWidth: 460, margin: '0 20px',
        background: '#fff', borderRadius: 18,
        boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
        overflow: 'hidden', fontFamily: 'inherit',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, padding: '24px 24px 20px' }}>
          <div style={{
            width: 44, height: 44, borderRadius: 12, background: '#e0e7ff',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#4f46e5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <line x1="19" y1="8" x2="19" y2="14"/>
              <line x1="22" y1="11" x2="16" y2="11"/>
            </svg>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', lineHeight: 1.3 }}>Add extra resource</div>
            <div style={{ fontSize: 13, color: '#64748b', marginTop: 3 }}>Bring in another teammate to help with this task</div>
          </div>
          <button onClick={onClose} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#cbd5e1', padding: 2, lineHeight: 1, marginTop: 2 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        {/* Body */}
        <div style={{ borderTop: '1px solid #f1f5f9', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          {error && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '8px 12px', fontSize: 13, color: '#dc2626', display: 'flex', justifyContent: 'space-between' }}>
              {error}
              <button onClick={onClearError} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontWeight: 700, fontSize: 14, lineHeight: 1 }}>×</button>
            </div>
          )}

          {/* User select */}
          <div>
            <label style={{ display: 'block', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#64748b', marginBottom: 6 }}>Select user</label>
            <div style={{ position: 'relative' }} ref={dropRef}>
              <button
                onClick={() => setDropOpen(v => !v)}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  gap: 8, padding: '10px 14px',
                  border: dropOpen ? '1.5px solid #818cf8' : '1.5px solid #e2e8f0',
                  borderRadius: 10, background: '#fff', cursor: 'pointer',
                  boxShadow: dropOpen ? '0 0 0 3px rgba(129,140,248,0.12)' : 'none',
                  outline: 'none', transition: 'border-color 0.15s, box-shadow 0.15s',
                  fontFamily: 'inherit',
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                  {selectedUser
                    ? <><UserAvatar name={selectedUser.name} /><span style={{ fontSize: 13.5, color: '#1e293b', fontWeight: 500 }}>{selectedUser.name}</span></>
                    : <span style={{ fontSize: 13.5, color: '#94a3b8' }}>Choose a teammate</span>
                  }
                </span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, transform: dropOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </button>

              {dropOpen && (
                <div style={{
                  position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 100,
                  background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 10,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.1)', overflow: 'hidden',
                }}>
                  <div style={{ padding: '8px 10px', borderBottom: '1px solid #f1f5f9' }}>
                    <input
                      autoFocus
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      placeholder="Search..."
                      style={{
                        width: '100%', border: '1.5px solid #e2e8f0', borderRadius: 7,
                        padding: '6px 10px', fontSize: 13, outline: 'none',
                        color: '#334155', fontFamily: 'inherit',
                      }}
                    />
                  </div>
                  <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                    {usersLoading
                      ? <div style={{ padding: '14px', textAlign: 'center' }}><CircularProgress size={20} /></div>
                      : filtered.length === 0
                        ? <div style={{ padding: '12px 14px', fontSize: 13, color: '#94a3b8' }}>No users found</div>
                        : filtered.map(u => (
                          <button key={u.user_id}
                            onClick={() => { setSelectedUser(u); setDropOpen(false); setSearch(''); }}
                            style={{
                              width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                              padding: '9px 14px', border: 'none', background: 'none',
                              cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                            onMouseLeave={e => e.currentTarget.style.background = 'none'}
                          >
                            <UserAvatar name={u.name} />
                            <div>
                              <div style={{ fontSize: 13.5, color: '#1e293b', fontWeight: 500 }}>{u.name}</div>
                              {u.department && <div style={{ fontSize: 11.5, color: '#94a3b8' }}>{u.department}</div>}
                            </div>
                          </button>
                        ))
                    }
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label style={{ display: 'block', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#64748b', marginBottom: 6 }}>Notes (optional)</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={3}
              placeholder="Any context they should know before jumping in..."
              style={{
                width: '100%', resize: 'none', border: '1.5px solid #e2e8f0', borderRadius: 10,
                padding: '10px 14px', fontSize: 13.5, color: '#1e293b',
                outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box',
                transition: 'border-color 0.15s, box-shadow 0.15s',
              }}
              onFocus={e => { e.target.style.borderColor = '#818cf8'; e.target.style.boxShadow = '0 0 0 3px rgba(129,140,248,0.12)'; }}
              onBlur={e => { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; }}
            />
          </div>
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8,
          borderTop: '1px solid #f1f5f9', background: '#f8fafc', padding: '14px 24px',
        }}>
          <button onClick={onClose} style={{
            padding: '9px 18px', border: 'none', borderRadius: 9, background: 'none',
            cursor: 'pointer', fontSize: 13.5, fontWeight: 600, color: '#64748b',
            fontFamily: 'inherit',
          }}
            onMouseEnter={e => e.target.style.background = '#f1f5f9'}
            onMouseLeave={e => e.target.style.background = 'none'}
          >
            Cancel
          </button>
          <button
            onClick={onAdd}
            disabled={!selectedUser || addLoading}
            style={{
              padding: '9px 20px', border: 'none', borderRadius: 9,
              background: selectedUser && !addLoading ? '#4f46e5' : '#f1f5f9',
              color: selectedUser && !addLoading ? '#fff' : '#94a3b8',
              cursor: selectedUser && !addLoading ? 'pointer' : 'not-allowed',
              fontSize: 13.5, fontWeight: 700, fontFamily: 'inherit',
              display: 'flex', alignItems: 'center', gap: 6, transition: 'background 0.15s',
            }}
            onMouseEnter={e => { if (selectedUser && !addLoading) e.currentTarget.style.background = '#4338ca'; }}
            onMouseLeave={e => { if (selectedUser && !addLoading) e.currentTarget.style.background = '#4f46e5'; }}
          >
            {addLoading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Add'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
const ExtraResourcesPanel = ({ segmentId, inline = false }) => {
  const [extraResources, setExtraResources] = useState([]);
  const [popoverAnchor, setPopoverAnchor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [notes, setNotes] = useState('');
  const [addLoading, setAddLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);

  const [canManage, setCanManage] = useState(false);

  const fetchExtraResources = useCallback(async () => {
    if (!segmentId) return;
    setLoading(true);
    try {
      const response = await api.get(`/api/segments/${segmentId}/extra-resources/`, {
        headers: { Authorization: `Token ${localStorage.getItem('Token')}` },
      });
      setExtraResources(response.data || []);
      setError(null);
    } catch (err) {
      setError('Failed to load extra resources');
    } finally {
      setLoading(false);
    }
  }, [segmentId]);

  const checkPermission = useCallback(async () => { setCanManage(true); }, []);

  const fetchAllUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      const response = await api.get(`users/names-departments/`, {
        headers: { Authorization: `Token ${localStorage.getItem('Token')}` },
      });
      const existingUserIds = extraResources.map(er => er.user_id);
      setAllUsers((response.data || []).filter(u => !existingUserIds.includes(u.user_id)));
    } catch {
      setAllUsers([]);
    } finally {
      setUsersLoading(false);
    }
  }, [extraResources]);

  useEffect(() => { fetchExtraResources(); checkPermission(); }, [fetchExtraResources, checkPermission]);

  const handleOpenDialog = () => {
    fetchAllUsers();
    setDialogOpen(true);
    setSelectedUser(null);
    setNotes('');
  };

  const handleAddResource = async () => {
    if (!selectedUser) return;
    setAddLoading(true);
    try {
      await api.post(
        `/api/segments/${segmentId}/add-extra-resource/`,
        { user_id: selectedUser.user_id, notes },
        { headers: { Authorization: `Token ${localStorage.getItem('Token')}` } }
      );
      setDialogOpen(false);
      fetchExtraResources();
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add resource');
    } finally {
      setAddLoading(false);
    }
  };

  const handleRemoveResource = async (userId) => {
    if (!window.confirm('Are you sure you want to remove this resource?')) return;
    try {
      await api.post(
        `/api/segments/${segmentId}/remove-extra-resource/`,
        { user_id: userId },
        { headers: { Authorization: `Token ${localStorage.getItem('Token')}` } }
      );
      fetchExtraResources();
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove resource');
    }
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch { return dateString; }
  };

  const resourceBody = (
    <Box sx={{ px: 2, pb: 2, pt: 1, minWidth: 280, maxWidth: 360 }}>
      {error && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>{error}</Alert>}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress size={24} /></Box>
      ) : extraResources.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>No extra resources assigned.</Typography>
      ) : (
        <List dense>
          {extraResources.map((resource) => (
            <ListItem key={resource.id} sx={{ px: 0 }}>
              <ListItemAvatar>
                <Avatar sx={{ width: 32, height: 32, fontSize: '0.875rem', bgcolor: colorFor(resource.user_name) }}>
                  {resource.user_name?.charAt(0).toUpperCase() || '?'}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={resource.user_name}
                secondary={
                  <Box>
                    <Typography variant="caption" color="text.secondary">Added by {resource.assigned_by_name} on {formatDate(resource.assigned_at)}</Typography>
                    {resource.notes && <Typography variant="caption" display="block" color="text.secondary">Note: {resource.notes}</Typography>}
                  </Box>
                }
              />
              {canManage && (
                <ListItemSecondaryAction>
                  <Tooltip title="Remove resource">
                    <IconButton edge="end" size="small" onClick={() => handleRemoveResource(resource.user_id)} color="error">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </ListItemSecondaryAction>
              )}
            </ListItem>
          ))}
        </List>
      )}
      {canManage && (
        <Button startIcon={<PersonAddIcon />} variant="outlined" size="small" onClick={handleOpenDialog} sx={{ mt: 1 }}>
          Add Resource
        </Button>
      )}
    </Box>
  );

  const modal = (
    <AddResourceModal
      open={dialogOpen}
      onClose={() => setDialogOpen(false)}
      allUsers={allUsers}
      usersLoading={usersLoading}
      selectedUser={selectedUser}
      setSelectedUser={setSelectedUser}
      notes={notes}
      setNotes={setNotes}
      onAdd={handleAddResource}
      addLoading={addLoading}
      error={error}
      onClearError={() => setError(null)}
    />
  );

  if (inline) {
    return (
      <>
        <Tooltip title="Extra Resources">
          <button
            onClick={(e) => setPopoverAnchor(e.currentTarget)}
            style={{ display: 'flex', alignItems: 'center', gap: 5, border: '1px solid #c7d2fe', borderRadius: 20, padding: '3px 10px', background: '#eef2ff', cursor: 'pointer', color: '#4f46e5', fontSize: 12, fontWeight: 600, fontFamily: 'inherit' }}
          >
            <GroupIcon sx={{ fontSize: 14 }} />
            Extra Resources
            <span style={{ background: '#4f46e5', color: '#fff', borderRadius: 10, padding: '0 6px', fontSize: 11, fontWeight: 700, minWidth: 18, textAlign: 'center' }}>{extraResources.length}</span>
          </button>
        </Tooltip>
        <Popover
          open={Boolean(popoverAnchor)}
          anchorEl={popoverAnchor}
          onClose={() => setPopoverAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{ sx: { borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.12)', mt: 0.5 } }}
        >
          <Box sx={{ px: 2, pt: 1.5, pb: 0.5, borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 1 }}>
            <GroupIcon color="primary" fontSize="small" />
            <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 700 }}>Extra Resources</Typography>
          </Box>
          {resourceBody}
        </Popover>
        {modal}
      </>
    );
  }

  return (
    <Box sx={{ borderTop: '1px solid #e0e0e0', mt: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1, cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
        onClick={() => setExpanded(!expanded)}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GroupIcon color="primary" fontSize="small" />
          <Typography variant="subtitle2" color="primary">Extra Resources</Typography>
          <Chip label={extraResources.length} size="small" color="primary" variant="outlined" />
        </Box>
        <IconButton size="small">{expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
      </Box>
      <Collapse in={expanded}>{resourceBody}</Collapse>
      {modal}
    </Box>
  );
};

export default ExtraResourcesPanel;
